import React, { useState, useEffect } from 'react';
import { Settings, FileText, Eye, RefreshCw, CheckCircle, XCircle, Clock, Users, Wrench, Webhook, Copy } from 'lucide-react';
import { formatDateTime } from '../../utils/dateUtils';

interface ExtractorStatus {
  connected: boolean;
  onboarded: boolean;
  onboardedAt?: string;
  autoCreateBills: boolean;
  defaultBillStatus: 'DRAFT' | 'SUBMITTED';
  syncIntervalMinutes: number;
  lastSyncAt?: string;
  lastExtractionId?: string;
  stats: {
    billsCreated: number;
    lastBillNumber?: string;
    lastBillCreatedAt?: string;
    syncedUsersCount: number;
  };
}

type TabType = 'settings' | 'templates' | 'extractions' | 'setup';

export const ExtractorManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('settings');
  const [status, setStatus] = useState<ExtractorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [templatesIframeUrl, setTemplatesIframeUrl] = useState<string>('');
  const [extractionsIframeUrl, setExtractionsIframeUrl] = useState<string>('');
  const [setupIframeUrl, setSetupIframeUrl] = useState<string>('');
  const [webhookConfig, setWebhookConfig] = useState<any>(null);
  const [webhookLogs, setWebhookLogs] = useState<any[]>([]);
  const [loadingWebhookLogs, setLoadingWebhookLogs] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    if (status?.connected) {
      fetchIframeUrls();
      fetchWebhookConfig();
      fetchWebhookLogs();
    }
  }, [status?.connected]);

  const fetchIframeUrls = async () => {
    try {
      const token = localStorage.getItem('token');

      const [templatesRes, extractionsRes, setupRes] = await Promise.all([
        fetch(`${apiUrl}/api/extractor/iframe-url?type=documents`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${apiUrl}/api/extractor/iframe-url?type=extractions`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${apiUrl}/api/extractor/iframe-url?type=settings`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      const templatesData = await templatesRes.json();
      const extractionsData = await extractionsRes.json();
      const setupData = await setupRes.json();

      if (templatesData.success) {
        setTemplatesIframeUrl(templatesData.data.url);
      }
      if (extractionsData.success) {
        setExtractionsIframeUrl(extractionsData.data.url);
      }
      if (setupData.success) {
        setSetupIframeUrl(setupData.data.url);
      }
    } catch (err) {
      console.error('Fetch iframe URLs error:', err);
    }
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/extractor/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setStatus(data.data);
      } else {
        setError(data.message || 'Failed to fetch Extractor status');
      }
    } catch (err) {
      console.error('Fetch Extractor status error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // COMMENTED OUT: Manual sync - using webhooks exclusively
  // const handleSync = async () => {
  //   try {
  //     setSyncing(true);
  //     setError(null);
  //     setSuccess(null);
  //     const token = localStorage.getItem('token');

  //     const response = await fetch(`${apiUrl}/api/extractor/sync`, {
  //       method: 'POST',
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });

  //     const data = await response.json();

  //     if (data.success) {
  //       setSuccess('Sync completed successfully');
  //       await fetchStatus();
  //     } else {
  //       setError(data.message || 'Failed to sync extractions');
  //     }
  //   } catch (err) {
  //     console.error('Sync error:', err);
  //     setError('Failed to sync extractions');
  //   } finally {
  //     setSyncing(false);
  //   }
  // };

  const handleOnboard = async () => {
    try {
      setOnboarding(true);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/extractor/onboard`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Account onboarded successfully! Refreshing...');
        setTimeout(() => {
          fetchStatus();
        }, 1500);
      } else {
        setError(data.message || 'Failed to onboard account');
      }
    } catch (err) {
      console.error('Onboard error:', err);
      setError('Failed to onboard account');
    } finally {
      setOnboarding(false);
    }
  };

  const fetchWebhookConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/extractor/webhook-url`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setWebhookConfig(data.data);
      }
    } catch (err) {
      console.error('Fetch webhook config error:', err);
    }
  };

  const fetchWebhookLogs = async () => {
    try {
      setLoadingWebhookLogs(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/extractor/webhook-logs?page=1&limit=10`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setWebhookLogs(data.data.items || []);
      }
    } catch (err) {
      console.error('Fetch webhook logs error:', err);
    } finally {
      setLoadingWebhookLogs(false);
    }
  };


  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleUpdateSettings = async (updates: {
    autoCreateBills?: boolean;
    defaultBillStatus?: 'DRAFT' | 'SUBMITTED';
    syncIntervalMinutes?: number;
  }) => {
    try {
      setUpdatingSettings(true);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/extractor/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Settings updated successfully');
        await fetchStatus();
      } else {
        setError(data.message || 'Failed to update settings');
      }
    } catch (err) {
      console.error('Update settings error:', err);
      setError('Failed to update settings');
    } finally {
      setUpdatingSettings(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="text-center py-12">
        <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Extractor Not Connected
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Your account hasn't been onboarded to 3PM Extractor yet. Click the button below to set it up now.
        </p>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg max-w-md mx-auto">
            <p className="text-sm text-error-800 dark:text-error-200">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 border border-green-200 dark:border-green-800 rounded-lg max-w-md mx-auto">
            <p className="text-sm text-success-800 dark:text-success-200">{success}</p>
          </div>
        )}
        
        <button
          onClick={handleOnboard}
          disabled={onboarding}
          className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {onboarding ? (
            <>
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              <span>Onboarding...</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              <span>Onboard to Extractor</span>
            </>
          )}
        </button>
        
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
          This will create a tenant and user in 3PM Extractor for your account.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex -mb-px space-x-8">
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            <FileText className="w-5 h-5" />
            <span>Document Templates</span>
          </button>
          <button
            onClick={() => setActiveTab('extractions')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'extractions'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            <Eye className="w-5 h-5" />
            <span>Extractions</span>
          </button>
          <button
            onClick={() => setActiveTab('setup')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'setup'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            <Wrench className="w-5 h-5" />
            <span>Setup</span>
          </button>
        </nav>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-error-800 dark:text-error-200">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm text-success-800 dark:text-success-200">{success}</p>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Connection Status */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Connection Status
              </h3>
              {status.connected ? (
                <div className="flex items-center space-x-2 text-success-600 dark:text-success-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Connected</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 text-error-600 dark:text-error-400">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">Not Connected</span>
                </div>
              )}
            </div>
            {status.onboardedAt && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Onboarded: {formatDateTime(status.onboardedAt)}
              </p>
            )}
          </div>

          {/* Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Settings
            </h3>
            <div className="space-y-4">
              {/* Auto-create Bills */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Auto-create bills from extractions
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Automatically create bill records when documents are extracted
                  </p>
                </div>
                <button
                  onClick={() =>
                    handleUpdateSettings({ autoCreateBills: !status.autoCreateBills })
                  }
                  disabled={updatingSettings}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    status.autoCreateBills
                      ? 'bg-primary-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      status.autoCreateBills ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Default Bill Status */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Default bill status
                </label>
                <select
                  value={status.defaultBillStatus}
                  onChange={(e) =>
                    handleUpdateSettings({
                      defaultBillStatus: e.target.value as 'DRAFT' | 'SUBMITTED',
                    })
                  }
                  disabled={updatingSettings}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="DRAFT">DRAFT</option>
                  <option value="SUBMITTED">SUBMITTED</option>
                </select>
              </div>

              {/* Sync Interval - Note: Fallback only when webhook not configured */}
              {/* Sync interval - COMMENTED OUT: Polling disabled, using webhooks exclusively */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Sync interval (minutes) - Fallback only
                </label>
                <select
                  value={status.syncIntervalMinutes}
                  onChange={(e) =>
                    handleUpdateSettings({ syncIntervalMinutes: parseInt(e.target.value) })
                  }
                  disabled={updatingSettings}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Used only as fallback when webhook is not configured. Webhooks provide real-time updates.
                </p>
              </div> */}

              {/* Manual Sync - COMMENTED OUT: Using webhooks exclusively */}
              {/* <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  <span>{syncing ? 'Syncing...' : 'Sync Now'}</span>
                </button>
                {status.lastSyncAt && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Last synced: {formatDateTime(status.lastSyncAt)}
                  </p>
                )}
              </div> */}
            </div>
          </div>

          {/* Webhook Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                <Webhook className="w-5 h-5" />
                <span>Webhook Information</span>
              </h3>
            </div>

            {webhookConfig && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Webhook URL
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={webhookConfig.webhookUrl || ''}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(webhookConfig.webhookUrl)}
                      className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg">
                  <p className="text-sm text-accent-800 dark:text-accent-200">
                    Configure webhooks in the <strong>Setup</strong> tab above. Go to Settings → Webhooks and add this URL to receive real-time extraction notifications.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Webhook Logs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                <Webhook className="w-5 h-5" />
                <span>Recent Webhook Events</span>
              </h3>
              <button
                onClick={fetchWebhookLogs}
                disabled={loadingWebhookLogs}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                title="Refresh logs"
              >
                <RefreshCw className={`w-4 h-4 ${loadingWebhookLogs ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {loadingWebhookLogs ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : webhookLogs.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                No webhook events yet
              </p>
            ) : (
              <div className="space-y-2">
                {webhookLogs.map((log: any) => (
                  <div
                    key={log._id}
                    className={`p-3 rounded-lg border ${
                      log.isValid
                        ? 'bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border-red-200 dark:border-red-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {log.isValid ? (
                          <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-error-600 dark:text-error-400" />
                        )}
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {log.eventType}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDateTime(log.createdAt)}
                      </span>
                    </div>
                    {log.error && (
                      <p className="text-xs text-error-600 dark:text-error-400 mt-1">{log.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Statistics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 mb-1">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm">Bills Created</span>
                </div>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {status.stats.billsCreated}
                </p>
              </div>
              <div>
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Last Bill</span>
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {status.stats.lastBillNumber || 'None'}
                </p>
                {status.stats.lastBillCreatedAt && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(status.stats.lastBillCreatedAt)}
                  </p>
                )}
              </div>
              <div>
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Synced Users</span>
                </div>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {status.stats.syncedUsersCount}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {templatesIframeUrl ? (
            <iframe
              src={templatesIframeUrl}
              width="100%"
              height="700"
              frameBorder="0"
              style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}
              title="Document Templates"
              allow="clipboard-write"
            />
          ) : (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          )}
        </div>
      )}

      {activeTab === 'extractions' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {extractionsIframeUrl ? (
            <iframe
              src={extractionsIframeUrl}
              width="100%"
              height="700"
              frameBorder="0"
              style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}
              title="Extractions"
            />
          ) : (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          )}
        </div>
      )}

      {activeTab === 'setup' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {setupIframeUrl ? (
            <iframe
              src={setupIframeUrl}
              width="100%"
              height="700"
              frameBorder="0"
              style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}
              title="Extractor Setup"
            />
          ) : (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
